/**
 * The family's dashboard: what needs them, across all their children.
 *
 * My Family is the record — contacts, enrollment, every grade. This is the
 * inbox in front of it, and on a quiet day it is deliberately almost empty.
 */
import { useCallback, useMemo, useState, type CSSProperties } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import {
  AlertTriangle, CalendarCheck, CheckCircle2, ChevronRight, FileWarning, Receipt, TrendingDown, Users,
} from 'lucide-react';
import { Button } from '~/components/ui/button';
import { Badge } from '~/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '~/components/ui/card';
import type { AppLayoutContext } from '~/types';
import { useViewAsFamily } from '~/hooks/useViewAsFamily';
import { useMyFamilyQuery } from '../MyFamily/useMyFamilyQuery';
import { useMyFamilyBillingQuery } from '../MyInvoices/useMyFamilyBillingQuery';
import { parentStudentStatus } from '../ParentEnrollment/parentStudentStatus';
import StudentSummaryLoader from './StudentSummaryLoader';
import {
  buildParentInbox, currentGrades, DEFAULT_ALERTS,
  type ParentInboxItem, type StudentSummary,
} from './parentInbox';

const ICONS: Record<ParentInboxItem['kind'], typeof Receipt> = {
  INVOICE_OVERDUE: Receipt,
  INVOICE_DUE: Receipt,
  ENROLLMENT_ACTION: FileWarning,
  MISSING_WORK: AlertTriangle,
  LOW_GRADE: TrendingDown,
};

const URGENCY_CLASSES: Record<string, string> = {
  danger: 'text-red-600 font-medium',
  warning: 'text-orange-600 font-medium',
  normal: 'text-muted-foreground',
};

function localDateString(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export default function ParentDashboard() {
  const { school, roleContext, setSelection } = useOutletContext<AppLayoutContext>();
  const selectedYear = roleContext?.selectedSchoolYear;
  // An admin viewing as this family reads the same dashboard, with the family
  // named and every link kept inside the view.
  const { familyId: viewAsFamilyId, basePath } = useViewAsFamily(school?.slug);

  const { data: familyData, loading: familyLoading } = useMyFamilyQuery(school?.id, selectedYear?.id, viewAsFamilyId ?? undefined);
  const { data: billingData } = useMyFamilyBillingQuery(school?.id, selectedYear?.id, viewAsFamilyId ?? undefined);
  const family = familyData?.myFamily;

  // Each child loads its own summary; a hook cannot run in a loop.
  const [summaries, setSummaries] = useState<Record<string, StudentSummary>>({});
  const handleLoaded = useCallback((summary: StudentSummary) => {
    setSummaries((current) => (
      current[summary.personId] === summary ? current : { ...current, [summary.personId]: summary }
    ));
  }, []);

  const children = family?.children ?? [];
  const invoices = billingData?.myFamilyBilling?.invoices ?? [];
  const alertSettings = familyData?.school?.parentAlertsSetting;

  const items = useMemo(
    () => buildParentInbox({
      students: children.map((child) => summaries[child.person.id]).filter(Boolean) as StudentSummary[],
      invoices: invoices.map((invoice) => ({
        id: invoice.invoiceId,
        status: invoice.displayStatus,
        dueDate: invoice.dueDate ? String(invoice.dueDate) : null,
        amountCents: invoice.amountCents,
        memo: invoice.memo,
      })),
      otherYearInvoices: (family?.openInvoices ?? []).map((invoice) => ({
        id: invoice.invoiceId,
        status: invoice.displayStatus,
        dueDate: invoice.dueDate ? String(invoice.dueDate) : null,
        amountCents: invoice.amountCents,
        memo: invoice.memo,
        schoolYear: { id: invoice.schoolYear.id, name: invoice.schoolYear.name },
      })),
      enrollment: family?.familyEnrollmentForYear
        ? {
          state: family.familyEnrollmentForYear.state,
          enrollmentKind: family.familyEnrollmentForYear.enrollmentKind,
          inviteToken: family.familyEnrollmentForYear.inviteToken,
          accessSource: family.familyEnrollmentForYear.accessSource,
        }
        : null,
      yearsNeedingYou: family?.yearsNeedingYou ?? [],
      basePath,
      today: localDateString(),
      alerts: alertSettings ?? DEFAULT_ALERTS,
    }),
    [children, summaries, invoices, family?.familyEnrollmentForYear, family?.yearsNeedingYou, family?.openInvoices, basePath, alertSettings]
  );

  if (familyLoading) return <div className="p-6 text-muted-foreground">Loading…</div>;

  if (!family) {
    return (
      <div className="p-6">
        <h1 className="text-3xl font-bold">Dashboard</h1>
        <p className="mt-4 text-muted-foreground">
          No family is associated with your account for this school year yet.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6">
      {children.map((child) => (
        <StudentSummaryLoader
          key={child.person.id}
          schoolId={school!.id}
          schoolYearId={selectedYear!.id}
          personId={child.person.id}
          name={child.person.fullName}
          onLoaded={handleLoaded}
        />
      ))}

      <div>
        <h1 className="text-3xl font-bold">{family.name}</h1>
        <p className="text-muted-foreground">
          {selectedYear?.name}
          {children.length > 0 && ` · ${children.length} student${children.length === 1 ? '' : 's'}`}
        </p>
      </div>

      <Card>
        <CardHeader className="p-4 pb-3 md:p-6 md:pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Needs you</CardTitle>
            {items.length > 0 && (
              <span className="text-sm text-muted-foreground">
                {items.length} item{items.length === 1 ? '' : 's'}
              </span>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-4 pt-0 md:p-6 md:pt-0">
          {items.length === 0 ? (
            <div className="flex items-center gap-3 rounded-lg border border-dashed p-6">
              <CheckCircle2 className="h-5 w-5 text-green-600" />
              <div>
                <p className="font-medium">You're all caught up</p>
                <p className="text-sm text-muted-foreground">
                  No missing work, no unpaid tuition, nothing waiting on your family.
                </p>
              </div>
            </div>
          ) : (
            <ul className="divide-y rounded-md border">
              {items.map((item) => {
                const Icon = ICONS[item.kind];
                // On a phone the button goes under the words, so they keep the
                // row's width and read in whole lines, the date included.
                return (
                  <li
                    key={item.key}
                    className="grid grid-cols-[auto_minmax(0,1fr)] items-start gap-x-3 gap-y-3 px-3 py-3.5 md:flex md:items-center md:gap-4 md:px-4"
                  >
                    <Icon className={`mt-0.5 h-5 w-5 shrink-0 md:mt-0 ${URGENCY_CLASSES[item.urgency]}`} />
                    <div className="min-w-0 md:flex-1">
                      <p className={`break-words ${URGENCY_CLASSES[item.urgency]}`}>{item.title}</p>
                      <p className="break-words text-sm text-muted-foreground md:truncate">{item.detail}</p>
                    </div>
                    <Button
                      asChild
                      size="sm"
                      variant={item.urgency === 'danger' ? 'default' : 'outline'}
                      className="col-start-2 justify-self-start"
                    >
                      {item.action.external
                        ? <a href={item.action.to}>{item.action.label}</a>
                        : (
                          <Link
                            to={item.action.to}
                            onClick={() => {
                              const yearId = item.action.schoolYearId;
                              if (yearId) setSelection({ schoolYearId: yearId });
                            }}
                          >
                            {item.action.label}
                          </Link>
                        )}
                    </Button>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>

      {children.length > 0 && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {children.map((child) => {
            const summary = summaries[child.person.id];
            const missing = summary?.missingAssignments.length ?? 0;
            const enrollment = child.person.studentEnrollmentForYear;
            return (
              <Card key={child.person.id}>
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    {child.person.fullName}
                  </CardTitle>
                  <CardDescription>
                    {enrollment?.gradeLevel ? `${enrollment.gradeLevel} · ` : ''}
                    {enrollment?.state ? parentStudentStatus(enrollment.state) : 'Not enrolled'}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center gap-2">
                    {missing > 0 ? (
                      <Badge variant="destructive" className="gap-1">
                        <AlertTriangle className="h-3 w-3" />
                        {missing} missing
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="gap-1">
                        <CalendarCheck className="h-3 w-3" />
                        Nothing missing
                      </Badge>
                    )}
                  </div>

                  {summary && currentGrades(summary.classGrades).length > 0 && (
                    <ul className="space-y-1 border-t pt-3 text-sm">
                      {currentGrades(summary.classGrades).map((grade) => (
                        <li key={grade.transcriptName} className="flex items-center justify-between gap-2">
                          <span className="truncate text-muted-foreground">{grade.transcriptName}</span>
                          <span
                            className="grade-color font-medium"
                            style={grade.result?.gradeColor
                              ? ({ '--grade-color': grade.result.gradeColor } as CSSProperties)
                              : undefined}
                          >
                            {(grade.result!.percentage as number).toFixed(1)}%
                            {grade.result?.letterGrade ? ` · ${grade.result.letterGrade}` : ''}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}

                  <Link
                    to={`${basePath}/students/${child.person.id}`}
                    className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
                  >
                    Full detail
                    <ChevronRight className="h-4 w-4" />
                  </Link>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
